// Provenance: repo:tds-reconciliation-portal:frontend/src/components/TxnMatcher.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings; the
// serial-keyed selection, rank-on-pick ordering and period gate carry unchanged.
//
// Match transactions by hand, inside the company they belong to. Two columns,
// each ordered by its own date, multi-select on both sides. The running totals
// sit above them because the residual is the thing that decides whether this
// match needs explaining, and finding that out after clicking is how a party
// came to report RECONCILED while carrying a gap.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { api, ApiError, postJson } from '../api/client'
import { useRun } from '../api/hooks'
import {
  canSubmit,
  closenessOrder,
  matchSelection,
  matchWindow,
  nearInWindow,
  openCountLabel,
  periodChoices,
  periodWarning,
  tieState,
} from '../api/txnMatch'
import type { MatchEntry, MatchPeriod } from '../api/txnMatch'
import { Btn } from './Modal'
import { Money } from './Money'
import { showToast } from './toast'

interface Row {
  /** The line in the merged sheet — the only identity that tells two
   *  physically identical entries apart. `fingerprint` is derived from content,
   *  so identical rows share one by design: keying the list on it made ticking
   *  one row tick every copy, and gave React duplicate keys into the bargain.
   *  The payload still travels as fingerprints, which is what `manual_match`
   *  takes; only the SELECTION is per row. */
  serial_no: number
  fingerprint: string
  party: string
  status: string
  tds_26as: string | null
  tds_books: string | null
  date_26as: string | null
  date_books: string | null
  amt_26as?: string | null
  amt_books?: string | null
  section: string | null
}

const day = (d: string | null) => (d ? d.slice(0, 10) : '—')

const selectCls =
  'rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary'

/** The quarters, halves and year this company has open entries in, each with
 *  its count. One aggregate call, cached per company. */
function usePeriodsOf(runId: string, party: string, enabled: boolean) {
  return useQuery({
    queryKey: ['match-periods', runId, party],
    queryFn: () => api<{ periods: MatchPeriod[] }>(
      `/runs/${runId}/difference/periods?party=${encodeURIComponent(party)}`),
    enabled,
  })
}

function useSide(runId: string, party: string, status: string,
                 range: { from: string; to: string } | null, enabled: boolean) {
  const sort = status === 'ONLY_26AS' ? 'date_26as' : 'date_books'
  const qs = new URLSearchParams({
    party, status, sort, dir: 'asc', bucket: 'unmatched', page: '1', size: '500',
  })
  if (range) {
    qs.set('from', range.from)
    qs.set('to', range.to)
  }
  return useQuery({
    queryKey: ['txn-match', runId, party, status, range?.from ?? '', range?.to ?? ''],
    queryFn: () => api<{ items: Row[]; total: number }>(
      `/runs/${runId}/difference?${qs}`),
    enabled,
  })
}

function Column({
  title, rows, side, picked, onToggle, near,
}: {
  title: string
  rows: Row[]
  side: '26as' | 'books'
  picked: Set<string>
  onToggle: (id: string) => void
  /** counterparts of the other side's pick that fall inside the run's date
   *  window — advisory, so these are emphasised and nothing is hidden */
  near: Set<string> | null
}) {
  // a transaction is a row of fields, and scanning amounts down a ragged
  // checkbox list is what tables exist for — the panes read like the
  // 26AS-vs-Books sheet. The whole row toggles the tick.
  return (
    <div className="min-w-80 flex-1 space-y-1">
      <p className="text-sm font-semibold text-foreground">
        {title} <span className="font-normal text-muted-foreground">({rows.length})</span>
      </p>
      <div className="rounded-lg border border-border">
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="w-7 px-2 py-1.5" />
                <th className="px-2 py-1.5 font-medium">Date</th>
                <th className="px-2 py-1.5 font-medium">Section</th>
                <th className="px-2 py-1.5 text-right font-medium">Amount</th>
                <th className="px-2 py-1.5 text-right font-medium">TDS</th>
              </tr>
            </thead>
            <tbody>
              {!rows.length && (
                <tr>
                  <td colSpan={5} className="px-2 py-2 text-muted-foreground">
                    Nothing unmatched here.
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const d = side === '26as' ? r.date_26as : r.date_books
                const v = side === '26as' ? r.tds_26as : r.tds_books
                const amt = side === '26as' ? r.amt_26as : r.amt_books
                // dimmed only when a pick exists AND this row is outside its
                // window; an undated row is never dimmed, because "cannot be
                // judged" is not "is far away"
                const faded = near != null && d != null && !near.has(r.fingerprint)
                const id = String(r.serial_no)
                return (
                  <tr
                    key={id}
                    onClick={() => onToggle(id)}
                    className="cursor-pointer border-t border-border hover:bg-muted/50"
                    style={{ opacity: faded ? 0.45 : 1 }}
                  >
                    <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={picked.has(id)}
                        onChange={() => onToggle(id)}
                        aria-label="pick entry"
                      />
                    </td>
                    <td className="whitespace-nowrap px-2 py-1 font-mono">{day(d)}</td>
                    <td className="px-2 py-1">{r.section ?? '—'}</td>
                    <td className="px-2 py-1 text-right">
                      {amt != null ? <Money v={amt} className="text-xs" /> : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-2 py-1 text-right"><Money v={v ?? '0'} bold className="text-xs" /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export function TxnMatcher({
  runId, party, n26, nBooks, windowDays = 7,
}: {
  runId: string
  party: string
  n26: number
  nBooks: number
  windowDays?: number
}) {
  // the run's OWN materiality, read from the run rather than defaulted here —
  // at a Rs 500 tolerance a screen judging at Rs 1 calls a party open that the
  // engine calls balanced, which is a defect this project has already had
  const runQ = useRun(runId)
  const tolerance = runQ.data?.tolerance ?? '1'
  const gate = matchWindow(n26, nBooks)
  const [period, setPeriod] = useState<string | null>(null)
  const [p26, setP26] = useState<Set<string>>(new Set())
  const [pbk, setPbk] = useState<Set<string>>(new Set())
  const [reason, setReason] = useState('')
  const qc = useQueryClient()

  // a company small enough to read is loaded whole; a big one waits for a
  // period, because a column of 29,548 rows is not a picker
  const ready = !gate.needsPeriod || period != null
  const periodsQ = usePeriodsOf(runId, party, gate.needsPeriod)
  const periods = periodsQ.data?.periods ?? []
  const chosen = periods.find((p) => p.key === period) ?? null
  const range = chosen ? { from: chosen.from, to: chosen.to } : null
  const q26 = useSide(runId, party, 'ONLY_26AS', range, ready)
  const qbk = useSide(runId, party, 'ONLY_BOOKS', range, ready)

  const match = useMutation({
    mutationFn: (body: { fingerprints: string[]; comment: string }) =>
      postJson(`/runs/${runId}/difference/manual-match`, body),
    onSuccess: () => {
      showToast({ color: 'green', title: 'Matched. It is recorded against your name.' })
      setP26(new Set()); setPbk(new Set()); setReason('')
      for (const k of ['txn-match', 'company-offers', 'diff-companies', 'difference',
        'difference-summary', 'reconciliation', 'cleared', 'match-suggestions']) {
        void qc.invalidateQueries({ queryKey: [k] })
      }
    },
    onError: (e) => showToast({
      color: 'red', title: 'Could not match',
      message: e instanceof ApiError ? e.message : String(e),
    }),
  })

  const rows26 = q26.data?.items ?? []
  const rowsbk = qbk.data?.items ?? []
  const asEntry = (r: Row, side: '26as' | 'books'): MatchEntry => ({
    fingerprint: r.fingerprint,
    tds: (side === '26as' ? r.tds_26as : r.tds_books) ?? '0',
    date: side === '26as' ? r.date_26as : r.date_books,
  })
  // selection is by ROW; the entries it turns into are what the server needs
  const sel26 = rows26.filter((r) => p26.has(String(r.serial_no)))
    .map((r) => asEntry(r, '26as'))
  const selbk = rowsbk.filter((r) => pbk.has(String(r.serial_no)))
    .map((r) => asEntry(r, 'books'))
  const tie = tieState(sel26, selbk, tolerance)

  // the far side is measured against the FIRST thing picked on this side —
  // one date, so the hint means something
  const nearBk = useMemo(
    () => (sel26.length === 1
      ? nearInWindow(sel26[0].date, rowsbk.map((r) => asEntry(r, 'books')), windowDays)
      : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sel26.length, sel26[0]?.date, rowsbk, windowDays])
  const near26 = useMemo(
    () => (selbk.length === 1
      ? nearInWindow(selbk[0].date, rows26.map((r) => asEntry(r, '26as')), windowDays)
      : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selbk.length, selbk[0]?.date, rows26, windowDays])

  // rank-on-pick: with exactly ONE entry picked on a side, the OTHER column
  // re-orders by closeness (date distance, then amount distance). Everything
  // stays listed; the near-window fade still marks the in-window rows.
  // Selection is keyed by serial_no, so reordering cannot drop a tick.
  const orderRows = (rows: Row[], side: '26as' | 'books', pick: MatchEntry | null) => {
    if (!pick) return rows
    const wrapped = rows.map((r) => ({
      r,
      date: side === '26as' ? r.date_26as : r.date_books,
      amount: (side === '26as' ? r.tds_26as : r.tds_books) ?? '0',
    }))
    return closenessOrder(wrapped, { date: pick.date, amount: pick.tds }).map((w) => w.r)
  }
  const rows26v = useMemo(
    () => orderRows(rows26, '26as', selbk.length === 1 ? selbk[0] : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows26, selbk.length, selbk[0]?.fingerprint])
  const rowsbkv = useMemo(
    () => orderRows(rowsbk, 'books', sel26.length === 1 ? sel26[0] : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowsbk, sel26.length, sel26[0]?.fingerprint])

  const toggle = (set: Set<string>, put: (s: Set<string>) => void) => (id: string) => {
    const n = new Set(set)
    if (n.has(id)) n.delete(id)
    else n.add(id)
    put(n)
  }

  if (gate.needsPeriod && period == null) {
    // the FY row's counts are OPEN entries through the same filter the lists
    // use — one arithmetic per screen; totals stand alone while it loads
    const fy = periods.find((p) => p.key === 'FY') ?? null
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          {openCountLabel(n26, nBooks, fy)} — too many to work at once. Pick a
          period; each one says what it holds.
        </p>
        {periodsQ.isPending && <p className="text-xs text-muted-foreground">Loading…</p>}
        <select
          className={`${selectCls} w-80`}
          value={period ?? ''}
          onChange={(e) => setPeriod(e.target.value || null)}
        >
          <option value="">Choose a period</option>
          {periodChoices(periods).map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>
    )
  }
  if (q26.isPending || qbk.isPending) {
    return <p className="text-sm text-muted-foreground">Loading…</p>
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground">
          Tick what is the same money on each side — any number a side. The dates are each
          entry&apos;s own.
        </p>
        {gate.needsPeriod && (
          <select
            className={`${selectCls} w-72`}
            value={period ?? ''}
            onChange={(e) => setPeriod(e.target.value || null)}
          >
            {periodChoices(periods).map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        )}
      </div>

      {/* Offered, warned about, never blocked. `FY` on this company is exactly
          the list the cap exists to prevent — and it is still selectable,
          because the cap is advice about size, not a rule about what the
          reconciler may look at. */}
      {periodWarning(periods, period) && (
        <p className="text-xs text-orange-700">{periodWarning(periods, period)}</p>
      )}

      {/* The running totals. Above the columns, not beside the button: the
          residual is what decides whether this match needs explaining, and
          learning that from a 422 after the click is how a party came to
          report RECONCILED while carrying a real difference. */}
      {tie.ready && (
        <div className="rounded-lg border border-border bg-muted/50 p-2.5">
          <div className="flex flex-wrap gap-6 text-xs text-muted-foreground">
            <span>26AS <Money v={tie.sum_26as} bold /></span>
            <span>books <Money v={tie.sum_books} bold /></span>
            <span className={tie.needsReason ? 'text-orange-700' : ''}>
              difference <Money v={tie.residual} bold />
              {!tie.needsReason && " — within this run's tolerance"}
            </span>
          </div>
          {tie.needsReason && (
            <label className="mt-1.5 block text-xs">
              <span className="block font-medium text-foreground">
                These differ by more than the run&apos;s tolerance of ₹{tolerance}
              </span>
              <span className="block text-muted-foreground">
                Matching them says they are the same money, so say why the amounts differ.
              </span>
              <input
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary"
                placeholder="short deduction, rounding, part payment against the same invoices…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-start gap-4">
        <Column title="26AS entries" rows={rows26v} side="26as" picked={p26}
          onToggle={toggle(p26, setP26)} near={near26} />
        <Column title="Books entries" rows={rowsbkv} side="books" picked={pbk}
          onToggle={toggle(pbk, setPbk)} near={nearBk} />
      </div>

      {(q26.data?.total ?? 0) > rows26.length || (qbk.data?.total ?? 0) > rowsbk.length ? (
        <p className="rounded-md bg-muted p-2 text-xs text-foreground">
          Showing the first 500 a side of {q26.data?.total} × {qbk.data?.total}. Narrow the
          month to see the rest.
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <Btn
          kind="primary"
          size="xs"
          disabled={!canSubmit(tie, reason)}
          loading={match.isPending}
          onClick={() => match.mutate({
            fingerprints: matchSelection(sel26, selbk),
            comment: reason.trim(),
          })}
        >
          Match {sel26.length} × {selbk.length}
        </Btn>
        {(p26.size > 0 || pbk.size > 0) && (
          <Btn kind="subtle" size="xs"
            onClick={() => { setP26(new Set()); setPbk(new Set()); setReason('') }}>
            Clear
          </Btn>
        )}
      </div>
    </div>
  )
}
